import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppStore } from '@/store/AppStore';
import Badge, { statusTone } from '@/components/base/Badge';
import AppointmentForm from './components/AppointmentForm';
import AppointmentDetail from './components/AppointmentDetail';
import EmptyState from '@/components/base/EmptyState';
import { formatTL } from '@/lib/format';
import { parseTrDate, toTrDate, weekdayLabel } from '@/lib/date';
import type { Appointment } from '@/types';

type View = 'Gün' | 'Hafta' | 'Ay' | 'Liste';

interface PrefillState {
  prefillCustomer?: string;
  prefillService?: string;
  prefillQuoteId?: string;
  prefillQuoteAmount?: number;
  prefillLocation?: string;
}

const DAY_HEADERS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

export default function Randevu() {
  const { appointments } = useAppStore();
  const location = useLocation();
  const [view, setView] = useState<View>('Liste');
  const [selectedDate, setSelectedDate] = useState('26 Ağustos 2026');
  const [selected, setSelected] = useState<Appointment | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [prefill, setPrefill] = useState<PrefillState>();

  useEffect(() => {
    const state = location.state as PrefillState | null;
    if (state?.prefillCustomer) {
      setPrefill(state);
      setFormOpen(true);
    }
  }, [location.state]);

  const sorted = useMemo(
    () =>
      [...appointments].sort((a, b) => {
        const da = parseTrDate(a.date).getTime();
        const db = parseTrDate(b.date).getTime();
        if (da !== db) return da - db;
        return a.startTime.localeCompare(b.startTime);
      }),
    [appointments],
  );

  const todayAppointments = sorted.filter((a) => a.date === selectedDate);

  // Week (Mon-Sun) around selectedDate
  const weekDays = useMemo(() => {
    const d = parseTrDate(selectedDate);
    const day = d.getDay(); // 0=Sun
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setDate(d.getDate() + mondayOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + i);
      return toTrDate(dayDate);
    });
  }, [selectedDate]);

  // August 2026 month grid
  const monthDays = useMemo(() => {
    const first = new Date(2026, 7, 1).getDay(); // 0=Sun
    const leading = (first + 6) % 7; // convert to Monday-first
    const cells: (string | null)[] = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= 31; d++) cells.push(`${d} Ağustos 2026`);
    return cells;
  }, []);

  const countOn = (date: string) => appointments.filter((a) => a.date === date).length;

  function openNewAppointment() {
    setPrefill({});
    setFormOpen(true);
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
            Operasyon
          </div>
          <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Randevu & Takvim</h1>
          <p className="mt-1 text-sm text-foreground-500">İş takvimini ve ekip planlamasını yönetin.</p>
        </div>
        <button
          type="button"
          onClick={openNewAppointment}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
        >
          <i className="ri-add-line" />
          Randevu Oluştur
        </button>
      </div>

      {/* View toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {(['Gün', 'Hafta', 'Ay', 'Liste'] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                view === v ? 'bg-white text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        {view !== 'Liste' && (
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground-700">
            <i className="ri-calendar-line text-accent-500" />
            {selectedDate}
            <span className="text-foreground-400">· {weekdayLabel(parseTrDate(selectedDate))}</span>
          </div>
        )}
      </div>

      {/* LISTE */}
      {view === 'Liste' && (
        <div className="space-y-3">
          {sorted.length === 0 ? (
            <div className="rounded-lg border border-background-200 bg-white">
              <EmptyState
                icon="ri-calendar-line"
                title="Randevu bulunmuyor."
                description="Yeni bir randevu oluşturun."
                actionLabel="Randevu Oluştur"
                onAction={openNewAppointment}
              />
            </div>
          ) : (
            sorted.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setSelected(a)}
                className="w-full rounded-lg border border-background-200 bg-white p-4 text-left transition-colors hover:border-primary-200 hover:bg-background-50"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex w-20 shrink-0 flex-col items-center rounded-lg bg-primary-50 py-2">
                    <span className="font-heading text-lg font-bold text-primary-700">{a.startTime}</span>
                    <span className="text-[11px] text-foreground-500">{a.duration}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-heading text-base font-bold text-foreground-950">{a.customerName}</span>
                      <Badge tone={statusTone(a.status)}>{a.status}</Badge>
                    </div>
                    <div className="mt-0.5 text-sm text-foreground-600">{a.service}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line" /> {a.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-group-line" /> {a.team}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" /> {a.date}
                      </span>
                    </div>
                  </div>
                  {a.quoteAmount > 0 && (
                    <div className="shrink-0 text-right">
                      <div className="text-xs text-foreground-400">Teklif</div>
                      <div className="font-heading text-base font-bold text-foreground-900">
                        {formatTL(a.quoteAmount)}
                      </div>
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* GÜN */}
      {view === 'Gün' && (
        <div className="rounded-lg border border-background-200 bg-white p-5">
          {todayAppointments.length === 0 ? (
            <EmptyState
              icon="ri-calendar-line"
              title="Bu gün için randevu bulunmuyor."
              description="Başka bir tarih seçin veya randevu oluşturun."
            />
          ) : (
            <ul className="space-y-2">
              {todayAppointments.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(a)}
                    className="flex w-full items-center gap-4 rounded-lg border border-background-100 p-3 text-left transition-colors hover:bg-background-50"
                  >
                    <span className="w-14 shrink-0 font-heading text-base font-bold text-primary-700">{a.startTime}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground-900">{a.customerName}</span>
                      <span className="block truncate text-xs text-foreground-500">
                        {a.service} · {a.team}
                      </span>
                    </span>
                    <Badge tone={statusTone(a.status)}>{a.status}</Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* HAFTA */}
      {view === 'Hafta' && (
        <div className="overflow-x-auto rounded-lg border border-background-200 bg-white">
          <div className="grid min-w-[840px] grid-cols-7 border-b border-background-100">
            {weekDays.map((d) => (
              <div
                key={d}
                className={`border-r border-background-100 p-2 text-center last:border-r-0 ${
                  d === selectedDate ? 'bg-primary-50' : ''
                }`}
              >
                <div className="text-xs font-semibold text-foreground-500">{DAY_HEADERS[(parseTrDate(d).getDay() + 6) % 7]}</div>
                <div className={`font-heading text-base font-bold ${d === selectedDate ? 'text-primary-700' : 'text-foreground-900'}`}>
                  {parseTrDate(d).getDate()}
                </div>
              </div>
            ))}
          </div>
          <div className="grid min-w-[840px] grid-cols-7">
            {weekDays.map((d) => {
              const dayApps = appointments.filter((a) => a.date === d).sort((a, b) => a.startTime.localeCompare(b.startTime));
              return (
                <div key={d} className="min-h-[180px] border-r border-background-100 p-2 last:border-r-0">
                  {dayApps.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setSelected(a)}
                      className="mb-2 block w-full rounded-md bg-accent-100/70 p-2 text-left hover:bg-accent-100"
                    >
                      <div className="text-xs font-bold text-accent-800">{a.startTime}</div>
                      <div className="truncate text-xs font-semibold text-foreground-800">{a.customerName}</div>
                      <div className="truncate text-[11px] text-foreground-500">{a.team}</div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AY */}
      {view === 'Ay' && (
        <div className="rounded-lg border border-background-200 bg-white p-5">
          <div className="mb-4 text-center">
            <span className="font-heading text-lg font-bold text-foreground-950">Ağustos 2026</span>
          </div>
          <div className="grid grid-cols-7 border-b border-background-100 pb-2">
            {DAY_HEADERS.map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-foreground-400">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthDays.map((cell, i) => {
              if (!cell) {
                return <div key={`empty-${i}`} className="min-h-[80px] border-r border-background-100" />;
              }
              const count = countOn(cell);
              const isSelected = cell === selectedDate;
              return (
                <button
                  key={cell}
                  type="button"
                  onClick={() => {
                    setSelectedDate(cell);
                    setView('Gün');
                  }}
                  className={`min-h-[80px] border-b border-r border-background-100 p-1.5 text-left transition-colors hover:bg-background-50 ${
                    isSelected ? 'bg-primary-50' : ''
                  }`}
                >
                  <span className={`text-xs font-semibold ${isSelected ? 'text-primary-700' : 'text-foreground-600'}`}>
                    {parseTrDate(cell).getDate()}
                  </span>
                  {count > 0 && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full bg-accent-500" />
                      <span className="text-[11px] font-semibold text-foreground-500">{count} randevu</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selected && <AppointmentDetail appointment={selected} onClose={() => setSelected(null)} />}
      {formOpen && (
        <AppointmentForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          prefillCustomer={prefill.prefillCustomer}
          prefillService={prefill.prefillService}
          prefillQuoteId={prefill.prefillQuoteId}
          prefillQuoteAmount={prefill.prefillQuoteAmount}
          prefillLocation={prefill.prefillLocation}
        />
      )}
    </div>
  );
}